import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { useDispatch } from 'react-redux';

import type { DtoV1Borrower1003BorrowerDeclarations } from '~/__generated__/api';
import { useSnackbar } from '~/common/components/Snackbar/snackbarStore';
import { NO, YES } from '~/common/constants';
import useUrl from '~/common/hooks/useUrl';
import { setLoanSummaryUpsertOrDelete } from '~/common/redux/actions';
import { typedApiClient } from '~/common/util/api-client';

import { useBorrower1003Store } from '../../../borrower1003Store';
import { BANKRUPYCY_CHAPTER } from '../../../constants';
import { borrower1003QueryKeys, use1003BorrowerData } from '../../../hooks';
import type { BorrowerDeclarationsData } from '../../../types';
import { booleansToStrings, stringsToBooleans } from '../../../utils';

const EMPTY_BORROWER_DECLARATIONS: DtoV1Borrower1003BorrowerDeclarations = {};

function normalizeBorrowerDeclarationsForSave(
  values: BorrowerDeclarationsData['formData'],
): BorrowerDeclarationsData['formData'] {
  const isPrimaryResidence = values.isPrimaryResidence === 'Yes';
  const hasOwnershipInterestInLastThreeYears =
    values.ownershipInterestInLastThreeYears === 'Yes';
  const hasUndisclosedMoneyBeingBorrowed =
    values.anyUndisclosedMoneyBeingBorrowed === 'Yes';
  const hasDeclaredBankruptcyInLastSevenYears =
    values.declaredBankruptcyInLastSevenYears === 'Yes';
  const hasOwnedPropertyInLastThreeYears =
    isPrimaryResidence && hasOwnershipInterestInLastThreeYears;

  const bankruptciesInLastSevenYears = values.bankruptciesInLastSevenYears;

  const getBankruptcyChapterValue = (
    chapter: (typeof BANKRUPYCY_CHAPTER)[keyof typeof BANKRUPYCY_CHAPTER],
  ): 'Yes' | 'No' | null => {
    if (!hasDeclaredBankruptcyInLastSevenYears) {
      return null;
    }

    return bankruptciesInLastSevenYears.includes(chapter) ? YES : NO;
  };

  return {
    ...values,
    amountOfUndisclosedMoney: hasUndisclosedMoneyBeingBorrowed
      ? values.amountOfUndisclosedMoney
      : null,
    ownershipInterestInLastThreeYears: isPrimaryResidence
      ? values.ownershipInterestInLastThreeYears
      : null,
    ownershipPropertyType: hasOwnedPropertyInLastThreeYears
      ? values.ownershipPropertyType
      : null,
    titleType: hasOwnedPropertyInLastThreeYears ? values.titleType : null,
    bankruptciesInLastSevenYears: undefined,
    bankruptcyChapter7: getBankruptcyChapterValue(BANKRUPYCY_CHAPTER.CHAPTER_7),
    bankruptcyChapter11: getBankruptcyChapterValue(
      BANKRUPYCY_CHAPTER.CHAPTER_11,
    ),
    bankruptcyChapter12: getBankruptcyChapterValue(
      BANKRUPYCY_CHAPTER.CHAPTER_12,
    ),
    bankruptcyChapter13: getBankruptcyChapterValue(
      BANKRUPYCY_CHAPTER.CHAPTER_13,
    ),
  };
}

export function useBorrowerDeclarationsFormData(borrowerId: number) {
  return use1003BorrowerData({
    select: useCallback(
      (s) => {
        const borrower = s.borrowers.find(
          (b) => b.customerRecordId === borrowerId,
        );

        const formData = booleansToStrings(
          borrower?.borrowerDeclarations ?? EMPTY_BORROWER_DECLARATIONS,
        );

        return {
          formData: {
            ...formData,
            bankruptciesInLastSevenYears: [
              formData.bankruptcyChapter7 === 'Yes'
                ? BANKRUPYCY_CHAPTER.CHAPTER_7
                : null,
              formData.bankruptcyChapter11 === 'Yes'
                ? BANKRUPYCY_CHAPTER.CHAPTER_11
                : null,
              formData.bankruptcyChapter12 === 'Yes'
                ? BANKRUPYCY_CHAPTER.CHAPTER_12
                : null,
              formData.bankruptcyChapter13 === 'Yes'
                ? BANKRUPYCY_CHAPTER.CHAPTER_13
                : null,
            ].filter(Boolean),
          },
          dropdownOptions: s.dropdownOptions,
          screenData: {
            underwritingMethod: s.underwritingMethod,
          },
        };
      },
      [borrowerId],
    ),
  });
}

export function useBorrowerDeclarationsMutation() {
  const { obfuscatedLoanIdentifier } = useUrl();
  const queryClient = useQueryClient();
  const showSnackbar = useSnackbar();
  const dispatch = useDispatch();

  return useMutation({
    mutationFn: async (values: BorrowerDeclarationsData['formData']) => {
      const normalizedValues = normalizeBorrowerDeclarationsForSave(values);

      await typedApiClient.v1.borrower1003ValidationUpdateBorrowerDeclarations({
        obfuscatedLoanIdentifier,
        borrowerDeclarations:
          stringsToBooleans<DtoV1Borrower1003BorrowerDeclarations>(
            normalizedValues,
          ),
      });
    },
    onSuccess: () => {
      useBorrower1003Store.setState({ selectedBorrowerId: null });

      queryClient.invalidateQueries({
        queryKey: borrower1003QueryKeys.borrowerData(obfuscatedLoanIdentifier),
      });

      dispatch(setLoanSummaryUpsertOrDelete(true));

      showSnackbar({
        message: 'Borrower declarations updated successfully.',
        severity: 'success',
      });
    },
    onError: () => {
      showSnackbar({
        message: 'Failed to update borrower declarations.',
        severity: 'error',
      });
    },
  });
}
